import { useId, useState } from 'react';
import { InputNumber, Segmented } from 'antd';

import { Button, Checkbox, Dialog, Field, Input, Select, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong, maLoi } from '../../../lib/errors';
import { LoiTrongHop, Nhom } from '../../tenant/components/phan-form';
import { KHOA_LOI_CHUNK, KHOA_LOI_MA_KHO } from '../constants';
import { usePhongBan, useSuaKho, useTaoKho } from '../hooks/useTriThuc';
import { CHUNK, kiemChunk, kiemMaKho } from '../nghiep-vu/tri-thuc';
import type { LoiChunk } from '../nghiep-vu/tri-thuc';
import type { CheDoChunk, KhoSua, KhoTao, KnowledgeBase } from '../types';

const DAI_TEN = 200;
const DAI_MO_TA = 1000;

/**
 * Tạo hoặc sửa một bộ tri thức. Mã và quy tắc cắt chunk chỉ đặt lúc tạo (backend từ chối sửa), nên ở
 * chế độ sửa hai phần đó chỉ hiện để đọc.
 */
export function FormKho({ kho, open, onClose }: { kho?: KnowledgeBase; open: boolean; onClose: () => void }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const tao = useTaoKho();
  const sua = useSuaKho();
  const phongBan = usePhongBan();
  const dangLuu = tao.isPending || sua.isPending;

  const [ma, setMa] = useState(kho?.code ?? '');
  const [ten, setTen] = useState(kho?.name ?? '');
  const [moTa, setMoTa] = useState(kho?.description ?? '');
  const [quanLy, setQuanLy] = useState<string | undefined>(kho?.managing_department.id);
  const [duocDung, setDuocDung] = useState<string[]>(kho?.permitted_departments.map((p) => p.id) ?? []);
  const [anNgoaiHieuLuc, setAnNgoaiHieuLuc] = useState(kho?.an_tai_lieu_ngoai_hieu_luc ?? false);
  const [che, setChe] = useState<CheDoChunk>('tu_dong');
  const [kichThuoc, setKichThuoc] = useState<number | null>(700);
  const [chongLan, setChongLan] = useState<number | null>(100);
  const [cauTruc, setCauTruc] = useState(true);
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{ loi: unknown; truong: Record<string, string> } | null>(null);

  const maLoiMa = kho ? null : kiemMaKho(ma);
  const loiChunk = kho ? {} : kiemChunk(che, kichThuoc, chongLan);
  // Ô còn trống chỉ báo sau khi bấm gửi; giá trị sai khoảng thì báo ngay khi gõ.
  const hienLoiChunk = (ma?: LoiChunk) => (ma && (daGui || !ma.endsWith('Thieu')) ? ma : undefined);
  const loiKichThuoc = hienLoiChunk(loiChunk.kichThuoc);
  const loiChongLan = hienLoiChunk(loiChunk.chongLan);
  const loiTen = daGui && !ten.trim() ? t('triThuc.kho.loiTenTrong') : undefined;
  const loiQuanLy = daGui && !quanLy ? t('triThuc.kho.loiQuanLyTrong') : undefined;
  const truongServer = loiServer?.truong ?? {};
  const loiMaServer = maLoi(loiServer?.loi) === 'TRI_THUC_KNOWLEDGE_BASE_CODE_EXISTS' ? dichLoi(loiServer?.loi) : undefined;
  const daGanTruong = Boolean(loiMaServer) || Object.keys(truongServer).length > 0;
  const loiChung = loiServer && !daGanTruong ? loiServer.loi : null;

  const thamSoChunk = { min: soVN(CHUNK.kichThuoc[0], locale), max: soVN(CHUNK.kichThuoc[1], locale),
    minCl: soVN(CHUNK.chongLan[0], locale), maxCl: soVN(CHUNK.chongLan[1], locale) };

  const doi = () => setLoiServer(null);

  const gui = async () => {
    setDaGui(true);
    if (maLoiMa || !ten.trim() || !quanLy || loiChunk.kichThuoc || loiChunk.chongLan || dangLuu) return;
    setLoiServer(null);
    try {
      if (kho) {
        const du: KhoSua = {
          name: ten.trim(),
          description: moTa.trim() || null,
          managing_department_id: quanLy,
          permitted_department_ids: duocDung,
          an_tai_lieu_ngoai_hieu_luc: anNgoaiHieuLuc,
        };
        await sua.mutateAsync({ id: kho.id, du });
      } else {
        const du: KhoTao = {
          code: ma.trim(),
          name: ten.trim(),
          description: moTa.trim() || null,
          managing_department_id: quanLy,
          permitted_department_ids: duocDung,
          an_tai_lieu_ngoai_hieu_luc: anNgoaiHieuLuc,
          chunk_mode: che,
          // Backend từ chối các ô chunk khi chế độ là tự động.
          ...(che === 'thu_cong' ? { chunk_size: kichThuoc, chunk_overlap: chongLan, chunk_gemini_cau_truc: cauTruc } : {}),
        };
        await tao.mutateAsync(du);
      }
      onClose();
    } catch (err) {
      setLoiServer({ loi: err, truong: loiTheoTruong(err) });
    }
  };

  const tuyChonPhongBan = (phongBan.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }));
  const loiPhongBan = phongBan.isError ? t('triThuc.loi.phongBan', { loi: dichLoi(phongBan.error) }) : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={kho ? t('triThuc.kho.suaTieuDe', { ten: kho.name }) : t('triThuc.kho.taoTieuDe')}
      width={560}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {Boolean(loiChung) && <LoiTrongHop>{dichLoi(loiChung)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangLuu}>
              {dangLuu ? t('chung.dangLuu') : kho ? t('triThuc.kho.luuBo') : t('triThuc.kho.taoBo')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {kho ? (
            <div className="space-y-1.5">
              <span className="block text-sm font-medium text-content-secondary">{t('triThuc.kho.fMa')}</span>
              <span className="block break-all font-mono text-sm text-content-primary">{kho.code}</span>
            </div>
          ) : (
            <Field
              label={t('triThuc.kho.fMa')}
              required
              hint={t('triThuc.kho.fMaGoiY')}
              error={(maLoiMa && (daGui || maLoiMa !== 'trong') ? t(KHOA_LOI_MA_KHO[maLoiMa]) : undefined)
                ?? loiMaServer ?? truongServer['/code']}
            >
              {(p) => (
                <Input
                  {...p}
                  autoFocus
                  className="font-mono"
                  maxLength={20}
                  value={ma}
                  onChange={(e) => {
                    setMa(e.target.value.toUpperCase());
                    doi();
                  }}
                />
              )}
            </Field>
          )}
          <Field label={t('triThuc.kho.fTen')} required error={loiTen ?? truongServer['/name']}>
            {(p) => (
              <Input
                {...p}
                autoFocus={Boolean(kho)}
                maxLength={DAI_TEN}
                value={ten}
                onChange={(e) => {
                  setTen(e.target.value);
                  doi();
                }}
              />
            )}
          </Field>
        </div>

        <Field label={t('triThuc.kho.fMoTa')} error={truongServer['/description']}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              maxLength={DAI_MO_TA}
              value={moTa}
              onChange={(e) => {
                setMoTa(e.target.value);
                doi();
              }}
            />
          )}
        </Field>

        <Field
          label={t('triThuc.kho.fQuanLy')}
          required
          error={loiQuanLy ?? loiPhongBan ?? truongServer['/managing_department_id']}
        >
          {(p) => (
            <Select<string>
              id={p.id}
              aria-describedby={p['aria-describedby']}
              status={p['aria-invalid'] ? 'error' : undefined}
              className="w-full"
              showSearch={{ optionFilterProp: 'label' }}
              placeholder={t('triThuc.kho.chonPhongBan')}
              loading={phongBan.isLoading}
              options={tuyChonPhongBan}
              value={quanLy}
              onChange={(v) => {
                setQuanLy(v);
                doi();
              }}
            />
          )}
        </Field>

        <Field
          label={t('triThuc.kho.fDuocDung')}
          hint={t('triThuc.kho.fDuocDungGoiY')}
          error={truongServer['/permitted_department_ids']}
        >
          {(p) => (
            <Select<string[]>
              id={p.id}
              aria-describedby={p['aria-describedby']}
              className="w-full"
              mode="multiple"
              showSearch={{ optionFilterProp: 'label' }}
              placeholder={t('triThuc.kho.moiPhongBan')}
              loading={phongBan.isLoading}
              options={tuyChonPhongBan}
              value={duocDung}
              onChange={(v) => {
                setDuocDung(v);
                doi();
              }}
            />
          )}
        </Field>

        <Checkbox checked={anNgoaiHieuLuc} onChange={(e) => setAnNgoaiHieuLuc(e.target.checked)}>
          <span className="text-sm text-content-primary">{t('triThuc.kho.fAnNgoaiHieuLuc')}</span>
          <span className="block text-xs text-content-muted">{t('triThuc.kho.fAnNgoaiHieuLucMo')}</span>
        </Checkbox>

        <div className="rounded-lg border border-default p-3">
          <Nhom tieuDe={t('triThuc.kho.chunkTieuDe')} moTa={t(kho ? 'triThuc.kho.chunkChiLucTao' : 'triThuc.kho.chunkMo')}>
            {kho ? (
              <p className="text-sm text-content-primary">
                {kho.chunk_mode === 'thu_cong'
                  ? t('triThuc.kho.chunkThuCongTomTat', {
                    kichThuoc: soVN(kho.chunk_size ?? 0, locale),
                    chongLan: soVN(kho.chunk_overlap ?? 0, locale),
                  })
                  : t('triThuc.kho.chunkTuDong')}
              </p>
            ) : (
              <>
                <Segmented<CheDoChunk>
                  block
                  aria-label={t('triThuc.kho.chunkTieuDe')}
                  options={[
                    { value: 'tu_dong', label: t('triThuc.kho.chunkTuDong') },
                    { value: 'thu_cong', label: t('triThuc.kho.chunkThuCong') },
                  ]}
                  value={che}
                  onChange={(v) => {
                    setChe(v);
                    doi();
                  }}
                />
                <p className="text-xs text-content-muted">
                  {t(che === 'tu_dong' ? 'triThuc.kho.chunkTuDongMo' : 'triThuc.kho.chunkThuCongMo')}
                </p>
                {che === 'thu_cong' && (
                  <div className="space-y-3 border-t border-default pt-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field
                        label={t('triThuc.kho.fKichThuoc')}
                        required
                        error={(loiKichThuoc ? t(KHOA_LOI_CHUNK[loiKichThuoc], thamSoChunk) : undefined)
                          ?? truongServer['/chunk_size']}
                      >
                        {(p) => (
                          <InputNumber<number>
                            {...p}
                            className="w-full"
                            value={kichThuoc}
                            onChange={(v) => {
                              setKichThuoc(v);
                              doi();
                            }}
                          />
                        )}
                      </Field>
                      <Field
                        label={t('triThuc.kho.fChongLan')}
                        required
                        error={(loiChongLan ? t(KHOA_LOI_CHUNK[loiChongLan], thamSoChunk) : undefined)
                          ?? truongServer['/chunk_overlap']}
                      >
                        {(p) => (
                          <InputNumber<number>
                            {...p}
                            className="w-full"
                            value={chongLan}
                            onChange={(v) => {
                              setChongLan(v);
                              doi();
                            }}
                          />
                        )}
                      </Field>
                    </div>
                    <p className="text-xs leading-relaxed text-content-muted">{t('triThuc.kho.chunkGoiY')}</p>
                    <Checkbox checked={cauTruc} onChange={(e) => setCauTruc(e.target.checked)}>
                      <span className="text-sm text-content-primary">{t('triThuc.kho.fCauTruc')}</span>
                      <span className="block text-xs text-content-muted">{t('triThuc.kho.fCauTrucMo')}</span>
                    </Checkbox>
                  </div>
                )}
              </>
            )}
          </Nhom>
        </div>
      </form>
    </Dialog>
  );
}
